<template>
    <div>
        <h1>Terms of Service</h1>
        <p><strong>Effective Date:</strong> 2025-03-01</p>
        <p><strong>Last Updated:</strong> 2025-07-27</p>

        <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
                By creating an account or using any part of Reeflendar, you confirm that you are at least 18 years old 
                (or have parental permission if younger) and that you agree to comply with these Terms.
            </p>
        </section>

        <section>
            <h2>2. Account Registration and Security</h2>
            <h3>2.1 User Accounts</h3>
            <ul>
                <li>To access certain features, you must register an account.</li>
                <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
                <li>You may log in using Google OAuth and agree to Google's terms.</li>
                <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
            </ul>

            <h3>2.2 Subscription and Payment (For Premium Features)</h3>
            <ul>
                <li>Reeflendar offers both free and premium accounts.</li>
                <li>Premium users gain access to advanced features such as unlimited logs and advanced analytics.</li>
                <li>Payments are processed securely through third-party providers like Stripe.</li>
                <li>All subscription payments are non-refundable, except where required by law.</li>
            </ul>
        </section>

        <section>
            <h2>3. User Conduct and Acceptable Use</h2>
            <p>By using Reeflendar, you agree <strong>not</strong> to:</p>
            <ul>
                <li>Use the platform for unlawful activities.</li>
                <li>Upload harmful, abusive, or misleading content.</li>
                <li>Attempt to disrupt, hack, or reverse-engineer the website.</li>
                <li>Share or distribute user-generated content without permission.</li>
                <li>Violate third-party rights, including privacy and intellectual property laws.</li>
            </ul>
            <p>Violating these rules may result in account suspension or termination.</p>
        </section>

        <section>
            <h2>4. Data Logging and Privacy</h2>
            <h3>4.1 Data Collection</h3>
            <p>Reeflendar allows users to log and track key aquarium parameters, upload photos, and add notes.</p>

            <h3>4.2 Data Ownership & Security</h3>
            <ul>
                <li>You own the data you enter into Reeflendar.</li>
                <li>By submitting content, you grant Reeflendar a license to store and display it as part of the service.</li>
                <li>We implement reasonable security measures but are not liable for unauthorized access.</li>
            </ul>

            <h3>4.3 Shareable Links</h3>
            <p>Users can generate read-only links that expire automatically after three weeks. Reeflendar is not responsible for shared content once a link is made public.</p>
        </section>

        <section>
            <h2>5. Intellectual Property</h2>
            <p>All Reeflendar trademarks, logos, and software are protected. You may not copy, distribute, or modify any part of the Website without our permission.</p>
        </section>

        <section>
            <h2>6. Service Availability & Modifications</h2>
            <h3>6.1 Availability</h3>
            <p>Reeflendar strives for 99.9% uptime but does not guarantee uninterrupted access. Temporary suspensions may occur for maintenance.</p>

            <h3>6.2 Modifications to Features</h3>
            <p>We reserve the right to update, modify, or discontinue features without notice.</p>
        </section>

        <section>
            <h2>7. Disclaimers & Limitation of Liability</h2>
            <h3>7.1 No Warranty</h3>
            <p>
                Reeflendar is provided "as is" without warranties of any kind. We do not guarantee that the service will always be accurate, error-free, or secure.
            </p>

            <h3>7.2 Limitation of Liability</h3>
            <ul>
                <li>
                    We are not liable for any loss of data, revenue, profits, or any other damages, including but not limited to loss of life, arising from the use of Reeflendar.
                </li>
                <li>
                    You are solely responsible for maintaining your own backup copies of any data you enter into Reeflendar. We strongly recommend that you regularly export and back up your data.
                </li>
                <li>
                    We are not responsible for system failures, outages, unauthorized account access, or inaccuracies in user-entered data.
                </li>
                <li>
                    By using Reeflendar.com, you agree to hold us harmless and indemnify us from any claims, damages, or losses, including those resulting in personal injury or loss of life, that may arise from your use of the service.
                </li>
            </ul>
        </section>

        <section>
            <h2>8. Termination of Use</h2>
            <p>We may suspend or terminate your account if you violate these Terms. Users may delete their accounts at any time.</p>
        </section>

        <section>
            <h2>9. Governing Law & Disputes</h2>
            <p>These Terms are governed by the laws of Illinois. Disputes must be resolved through binding arbitration (except where prohibited by law).</p>
        </section>

        <section>
            <h2>10. Contact Information</h2>
            <p>For any questions, contact us at <a href="mailto:info@reeflendar.com">info@reeflendar.com</a>.</p>
        </section>
    </div>
</template>

<script setup>
import { useHead } from '@vueuse/head';

useHead({
  title: 'Reeflendar.com - Terms of Service',
  meta: [
    { name: 'description', content: 'Terms of service for Reeflendar.' },
    { name: 'keywords', content: 'terms, service, aquarium' },
  ],
});
</script>